import { CSSProperties, ReactNode } from 'react';
import ActingAsUserMessage from './acting-as-user-message';
import SharedSvg from './shared-svg';

import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Close from '@mui/icons-material/Close';
import type { SxProps, Theme } from '@mui/material/styles';

interface SharedDialogButton {
  action: () => void;
  title: string;
  color?: 'primary' | 'warning' | 'error';
  loading?: boolean;
  disabled?: boolean;
  startIcon?: ReactNode;
  sx?: SxProps<Theme>;
}

interface SharedDialogProps {
  open: boolean;
  handleClose: () => void;
  title: string;
  /**
   * DO NOT add padding or margin on the root element; DialogContent in SharedDialog owns spacing.
   * When including form fields, set autoFocus on the first input.
   */
  dialogContent?: ReactNode;
  /** When set, shows an "Acting as" message above dialogContent. */
  actingAs?: string;
  primaryButton: SharedDialogButton;
  secondaryButton?: SharedDialogButton;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  noCancel?: boolean;
  buttonAlignment?: CSSProperties['justifyContent'];
}

export default function SharedDialogOld({
  open,
  handleClose,
  title,
  dialogContent,
  actingAs,
  primaryButton,
  secondaryButton,
  maxWidth,
  noCancel = false,
  buttonAlignment,
}: SharedDialogProps) {
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth={maxWidth ?? 'sm'}
      // https://github.com/mui/material-ui/issues/33004#issuecomment-1455260156
      // autoFocus on the first input will not work if disableRestoreFocus isn't true
      disableRestoreFocus
    >
      <DialogTitle
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
        }}
      >
        <SharedSvg sx={{ mr: 2 }} />
        <Typography variant="h6" component="span">
          {title}
        </Typography>
      </DialogTitle>

      <IconButton
        sx={{ position: 'absolute', top: 0, right: 0 }}
        onClick={handleClose}
      >
        <Close />
      </IconButton>

      <Divider />

      <DialogContent sx={{ p: 3, '& strong': { fontWeight: 700 } }}>
        {actingAs && <ActingAsUserMessage userEmail={actingAs} />}
        {dialogContent}
      </DialogContent>

      <DialogActions sx={{ justifyContent: buttonAlignment ?? 'flex-end' }}>
        {!noCancel && (
          <Button
            onClick={handleClose}
            sx={{
              color: 'text.secondary',
              '&:hover': {
                background: (theme) => theme.palette.background.default,
              },
            }}
          >
            Cancel
          </Button>
        )}

        {secondaryButton && (
          <Button
            loading={secondaryButton.loading ?? false}
            disabled={secondaryButton.disabled ?? false}
            variant="outlined"
            onClick={secondaryButton.action}
            color="primary"
            sx={secondaryButton.sx}
          >
            {secondaryButton.title}
          </Button>
        )}
        <Button
          loading={primaryButton.loading ?? false}
          variant="contained"
          onClick={primaryButton.action}
          disabled={primaryButton.disabled ?? false}
          color={primaryButton.color}
          startIcon={primaryButton.startIcon}
          sx={primaryButton.sx}
        >
          {primaryButton.title}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
